// Item customization (modifier) catalog.
//
// Options attach by CATEGORY, so all mains share one spice/add-ons/portion set
// and all drinks share size/sugar — editable in one place. The server mirrors
// this in functions/src/modifiers.ts and re-prices from it, so the deltas here
// are the source of truth for both the sheet preview and the charged total.

import type {
  Category,
  ModifierGroup,
  SelectedModifier,
} from "../lib/types";

const SPICE: ModifierGroup = {
  id: "spice",
  name: "Spice level",
  nameAr: "درجة الحرارة",
  type: "single",
  required: true,
  options: [
    { id: "mild", name: "Mild", nameAr: "خفيف", priceDelta: 0 },
    { id: "medium", name: "Medium", nameAr: "وسط", priceDelta: 0 },
    { id: "hot", name: "Hot", nameAr: "حار", priceDelta: 0 },
    { id: "xhot", name: "Extra hot", nameAr: "حار جداً", priceDelta: 0 },
  ],
};

const ADDONS: ModifierGroup = {
  id: "addons",
  name: "Add-ons",
  nameAr: "إضافات",
  type: "multi",
  max: 4,
  options: [
    { id: "extra-sauce", name: "Extra sauce", nameAr: "صلصة إضافية", priceDelta: 0.25 },
    { id: "extra-rice", name: "Extra rice", nameAr: "رز إضافي", priceDelta: 0.5 },
    { id: "fried-egg", name: "Fried egg", nameAr: "بيضة مقلية", priceDelta: 0.2 },
    { id: "cheese", name: "Cheese", nameAr: "جبنة", priceDelta: 0.4 },
  ],
};

const PORTION: ModifierGroup = {
  id: "portion",
  name: "Portion",
  nameAr: "الحجم",
  type: "single",
  required: true,
  options: [
    { id: "regular", name: "Regular", nameAr: "عادي", priceDelta: 0 },
    { id: "large", name: "Large", nameAr: "كبير", priceDelta: 0.75 },
  ],
};

const DRINK_SIZE: ModifierGroup = {
  id: "size",
  name: "Size",
  nameAr: "الحجم",
  type: "single",
  required: true,
  // Distinct ids from PORTION's regular/large so the delta is unambiguous
  // (drink Large is +0.25, portion Large is +0.75) — the server keys on id.
  options: [
    { id: "size-regular", name: "Regular", nameAr: "عادي", priceDelta: 0 },
    { id: "size-large", name: "Large", nameAr: "كبير", priceDelta: 0.25 },
  ],
};

const SUGAR: ModifierGroup = {
  id: "sugar",
  name: "Sugar",
  nameAr: "السكر",
  type: "single",
  required: true,
  options: [
    { id: "normal", name: "Normal", nameAr: "عادي", priceDelta: 0 },
    { id: "less", name: "Less sugar", nameAr: "سكر أقل", priceDelta: 0 },
    { id: "none", name: "No sugar", nameAr: "بدون سكر", priceDelta: 0 },
  ],
};

/** Category → modifier groups. Categories not listed have no options. */
export const MODIFIERS_BY_CATEGORY: Partial<Record<Category, ModifierGroup[]>> = {
  chinese: [SPICE, ADDONS, PORTION],
  seafood: [SPICE, ADDONS, PORTION],
  veg: [SPICE, ADDONS, PORTION],
  beverages: [DRINK_SIZE, SUGAR],
};

export function optionsForCategory(cat: Category): ModifierGroup[] | undefined {
  return MODIFIERS_BY_CATEGORY[cat];
}

function round3(n: number): number {
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}

/** Unit price = base + the sum of every chosen option's delta. */
export function unitPriceWith(base: number, mods: SelectedModifier[] | undefined): number {
  const add = (mods ?? []).reduce((s, m) => s + m.priceDelta, 0);
  return round3(base + add);
}

/**
 * Stable line identity. Plain items (no options, no notes) key on itemId alone,
 * so the existing add / stepper / qtyOf paths keep working unchanged. Variants
 * append a signature so they live as separate cart lines.
 */
export function lineUid(
  itemId: string,
  mods?: SelectedModifier[],
  notes?: string,
): string {
  const sig = (mods ?? [])
    .map((m) => m.optionId)
    .sort()
    .join(",");
  const n = (notes ?? "").trim();
  return sig || n ? `${itemId}|${sig}|${n}` : itemId;
}

/** Short one-line summary of chosen options, for cart / order display. */
export function modifierSummary(
  mods: SelectedModifier[] | undefined,
  lang: "en" | "ar",
): string {
  if (!mods || mods.length === 0) return "";
  return mods.map((m) => (lang === "ar" ? m.optionNameAr : m.optionName)).join(" · ");
}
